// Coolify autodeploy test 2026-03-05
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './contexts/AuthContext';
import { AirlineProvider } from './contexts/AirlineContext';
import { DeviceProvider } from './contexts/DeviceContext';
import ProtectedRoute from './components/ProtectedRoute';
import DashboardLayout from './components/DashboardLayout';
import SuperAdminLayout from './components/layouts/SuperAdminLayout';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Dashboard from './pages/Dashboard';
import Files from './pages/Files';
import Sessions from './pages/Sessions';
import SessionDetails from './pages/SessionDetails';
import EditAssessmentPilot from './pages/EditAssessmentPilot';
import EditAssessmentInstructor from './pages/EditAssessmentInstructor';
import EditDebriefingGuide from './pages/EditDebriefingGuide';
import Admin from './pages/Admin';
import TraineeDashboard from './pages/TraineeDashboard';
import TraineeAssessmentView from './pages/TraineeAssessmentView';
import SessionWorkflow from './pages/SessionWorkflow';
import AirlinesAdmin from './pages/admin/AirlinesAdmin';
import FleetsAdmin from './pages/admin/FleetsAdmin';
import DocTypesAdmin from './pages/admin/DocTypesAdmin';
import AirlineDocTypeOverridesAdmin from './pages/admin/AirlineDocTypeOverridesAdmin';
import DocumentsAdmin from './pages/admin/DocumentsAdmin';
import RetrievalPoliciesAdmin from './components/admin/RetrievalPoliciesAdmin';
import RetrievalDiagnostics from './components/admin/RetrievalDiagnostics';
import ApiCostsDashboard from './features/api-monitoring/components/ApiCostsDashboard';
import PipelineErrorsAdmin from './pages/admin/PipelineErrorsAdmin';
import TestMergeU2 from './pages/TestMergeU2';
import Settings from './pages/Settings';
import { isMobileDevice } from './utils/device';
import './index.css';

// Create QueryClient instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (renamed from cacheTime in v5)
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <DeviceProvider>
        <AuthProvider>
          <AirlineProvider>
            <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          
          {/* Protected routes with Dashboard Layout */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Dashboard />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/files"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Files />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/sessions"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Sessions />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/sessions/:id"
            element={
              <ProtectedRoute>
                <DashboardLayout hideChrome>
                  <SessionDetails />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/sessions/:sessionId/edit-pilot-assessment"
            element={
              <ProtectedRoute>
                <DashboardLayout hideChrome>
                  <EditAssessmentPilot />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/sessions/:sessionId/edit-instructor-assessment"
            element={
              <ProtectedRoute>
                <DashboardLayout hideChrome>
                  <EditAssessmentInstructor />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/sessions/:sessionId/debriefing-guide"
            element={
              <ProtectedRoute>
                <DashboardLayout hideChrome>
                  <EditDebriefingGuide />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Admin />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* Super Admin Routes */}
          <Route
            path="/admin/airlines"
            element={
              <ProtectedRoute>
                <SuperAdminLayout>
                  <AirlinesAdmin />
                </SuperAdminLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/fleets"
            element={
              <ProtectedRoute>
                <SuperAdminLayout>
                  <FleetsAdmin />
                </SuperAdminLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/doc-types"
            element={
              <ProtectedRoute>
                <SuperAdminLayout>
                  <DocTypesAdmin />
                </SuperAdminLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/airline-overrides"
            element={
              <ProtectedRoute>
                <SuperAdminLayout>
                  <AirlineDocTypeOverridesAdmin />
                </SuperAdminLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/documents"
            element={
              <ProtectedRoute>
                <SuperAdminLayout>
                  <DocumentsAdmin />
                </SuperAdminLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/retrieval-policies"
            element={
              <ProtectedRoute>
                <SuperAdminLayout>
                  <RetrievalPoliciesAdmin />
                </SuperAdminLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/retrieval-diagnostics"
            element={
              <ProtectedRoute>
                <SuperAdminLayout>
                  <RetrievalDiagnostics />
                </SuperAdminLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/retrieval-policies"
            element={
              <ProtectedRoute>
                <SuperAdminLayout>
                  <RetrievalPoliciesAdmin />
                </SuperAdminLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/api-costs"
            element={
              <ProtectedRoute>
                <SuperAdminLayout>
                  <ApiCostsDashboard />
                </SuperAdminLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/pipeline-errors"
            element={
              <ProtectedRoute>
                <SuperAdminLayout>
                  <PipelineErrorsAdmin />
                </SuperAdminLayout>
              </ProtectedRoute>
            }
          />

          <Route path="/test-merge-u2" element={<TestMergeU2 />} />

          {/* Mobile Workflow (fullscreen, no DashboardLayout) */}
          <Route
            path="/session-workflow/:id"
            element={
              <ProtectedRoute>
                <SessionWorkflow />
              </ProtectedRoute>
            }
          />

          {/* Settings */}
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Settings />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* Trainee Portal Routes */}
          <Route
            path="/trainee/dashboard"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <TraineeDashboard />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/trainee/assessment/:sessionId"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <TraineeAssessmentView />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          
          {/* Default redirect — mobile goes to sessions, desktop to dashboard */}
          <Route path="/" element={<Navigate to={isMobileDevice() ? '/sessions' : '/dashboard'} replace />} />
          
          {/* 404 - redirect to login */}
          <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
          </AirlineProvider>
        </AuthProvider>
        </DeviceProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;

